import React from "react";
import tottiesLogo from "../img/totties_text.svg";
import "./Landing.css";

interface LandingProps {
  onNavigate: (tab: string) => void;
  onOpenAuth: () => void;
  user?: { email?: string; username?: string } | null;
}

const tools = [
  {
    id: "affordability",
    tab: "#affordability",
    label: "Affordability",
    headline: "Can you afford it?",
    description:
      "Calculate how much home or car you can comfortably afford based on your income, expenses, and interest rates.",
    icon: (
      <svg viewBox="0 0 48 48" fill="none" aria-hidden="true">
        <rect
          x="6"
          y="14"
          width="36"
          height="26"
          rx="4"
          stroke="currentColor"
          strokeWidth="2.5"
        />
        <path d="M6 22h36" stroke="currentColor" strokeWidth="2.5" />
        <path
          d="M16 30h4M26 30h6"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
        />
        <path
          d="M24 6l-8 8h16l-8-8z"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinejoin="round"
        />
      </svg>
    ),
    colorClass: "bento-affordability",
    size: "bento-wide",
  },
  {
    id: "tax",
    tab: "#tax",
    label: "Tax Calculator",
    headline: "Know your SARS tax",
    description:
      "Estimate your South African income tax, UIF, and take-home pay for the current tax year.",
    icon: (
      <svg viewBox="0 0 48 48" fill="none" aria-hidden="true">
        <path
          d="M12 6h24l6 8H6l6-8z"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinejoin="round"
        />
        <rect
          x="6"
          y="14"
          width="36"
          height="28"
          rx="3"
          stroke="currentColor"
          strokeWidth="2.5"
        />
        <path
          d="M18 28l4 4 8-8"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
    colorClass: "bento-tax",
    size: "bento-tall",
  },
  {
    id: "monthly",
    tab: "#monthlyfinances",
    label: "Monthly Finances",
    headline: "Track every rand",
    description:
      "Plan shared or personal monthly budgets, split expenses, and see exactly where your money goes.",
    icon: (
      <svg viewBox="0 0 48 48" fill="none" aria-hidden="true">
        <circle
          cx="24"
          cy="24"
          r="18"
          stroke="currentColor"
          strokeWidth="2.5"
        />
        <path
          d="M24 14v10l6 6"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M14 24h2M32 24h2M24 14v2M24 32v2"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        />
      </svg>
    ),
    colorClass: "bento-monthly",
    size: "bento-wide",
  },
  {
    id: "bond",
    tab: "#affordability",
    label: "Bond Repayment",
    headline: "Plan your bond",
    description:
      "See your monthly repayments, total interest paid, and how rate changes affect your loan over time.",
    icon: (
      <svg viewBox="0 0 48 48" fill="none" aria-hidden="true">
        <path
          d="M8 42V22L24 8l16 14v20"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinejoin="round"
        />
        <rect
          x="18"
          y="30"
          width="12"
          height="12"
          rx="2"
          stroke="currentColor"
          strokeWidth="2.5"
        />
        <path
          d="M24 30v12"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        />
      </svg>
    ),
    colorClass: "bento-bond",
    size: "bento-tall",
  },
];

const Landing: React.FC<LandingProps> = ({ onNavigate, onOpenAuth }) => {
  return (
    <div className="landing">
      {/* ── Hero ──────────────────────────────────────────────────── */}
      <section className="landing-hero">
        <div className="hero-blobs" aria-hidden="true">
          <div className="hero-blob hero-blob-1" />
          <div className="hero-blob hero-blob-2" />
          <div className="hero-blob hero-blob-3" />
        </div>

        <div className="hero-content">
          <h1 className="hero-headline">
            Smarter money
            <br />
            <span className="hero-headline-accent">
              decisions for South Africans
            </span>
          </h1>
          <p className="hero-subline">
            Purpose-built financial calculators. No sign-up required.
            <br />
            Built with accuracy and simplicity in mind.
          </p>
          <div className="hero-ctas">
            <button
              className="hero-cta-primary"
              onClick={() => onNavigate("#affordability")}
            >
              Explore tools
            </button>
            <button className="hero-cta-secondary" onClick={onOpenAuth}>
              Save your work
            </button>
          </div>
        </div>

        <div className="hero-scroll-hint" aria-hidden="true">
          <span />
        </div>
      </section>

      {/* ── Bento grid ────────────────────────────────────────────── */}
      <section className="landing-bento">
        <div className="bento-header">
          <h2>Everything you need in one place</h2>
          <p>Easy to understand, zero jargon. Pick the one you need.</p>
        </div>

        <div className="bento-grid">
          {tools.map((tool) => (
            <button
              key={tool.id}
              className={`bento-card ${tool.colorClass} ${tool.size}`}
              onClick={() => onNavigate(tool.tab)}
              aria-label={`Open ${tool.label}`}
            >
              <div className="bento-card-inner">
                <div className="bento-icon">{tool.icon}</div>
                <div className="bento-text">
                  <span className="bento-label">{tool.label}</span>
                  <h3 className="bento-headline">{tool.headline}</h3>
                  <p className="bento-desc">{tool.description}</p>
                </div>
                <span className="bento-cta">Open calculator →</span>
              </div>
            </button>
          ))}
        </div>
      </section>

      {/* ── Footer strip ─────────────────────────────────────────── */}
      <footer className="landing-footer">
        <nav className="landing-footer-nav" aria-label="Footer navigation">
          <button className="landing-footer-link" onClick={() => onNavigate("#affordability")}>Affordability</button>
          <button className="landing-footer-link" onClick={() => onNavigate("#monthlyfinances")}>Monthly Finances</button>
          <button className="landing-footer-link" onClick={() => onNavigate("#tax")}>Tax</button>
          <button className="landing-footer-link" onClick={() => onNavigate("#about")}>About</button>
        </nav>
        <p>
          Find this useful?{" "}
          <a
            href="https://www.buymeacoffee.com/totties"
            target="_blank"
            rel="noopener noreferrer"
          >
            Buy me a coffee ☕
          </a>
        </p>
        <p className="landing-footer-legal">
          Totties is for informational purposes only and does not constitute
          financial advice.
        </p>
      </footer>
    </div>
  );
};

export default Landing;
